import { zodResolver } from '@hookform/resolvers/zod';
import { Modal, message } from 'antd';
import React from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import TitleText from '../../atoms/TitleText';
import FormField from '../../FormField/FormField';
import InfoText from '../../atoms/InfoText';
import Button from '../../atoms/Button';
import { useKeepReviewMutation } from '../../../store/services/Review';

export default function KeepReview({
  refetch,
  isModalOpen,
  handleCloseModal,
  reviewId,
  refetchCallBack,
}: {
  isModalOpen: boolean;
  handleCloseModal: () => void;
  refetch: () => Promise<{ data?: unknown; error?: unknown }>;
  reviewId: string;
  refetchCallBack: () => void;
}) {
  const { t } = useTranslation();
  const required_error = t('components.validations.minLength', { value: 2 });
  const max_size_error = t('components.validations.maxLength', { value: 50 });

  const [keepReview, { isLoading }] = useKeepReviewMutation();

  const ZodSchema = z.object({
    comments: z.string().min(2, required_error).max(50, max_size_error),
  });
  type formField = z.infer<typeof ZodSchema>;

  const methods = useForm<formField>({
    mode: 'all',
    resolver: zodResolver(ZodSchema),
    defaultValues: { comments: '' },
  });

  const onSubmit = async () => {
    try {
      await keepReview({
        reviewId: reviewId,
        comments: methods.getValues().comments,
      }).unwrap();

      message.success(t('reviews.keepSuccess', 'Feedback kept successfully'));

      refetchCallBack?.();
      handleCloseModal?.();

      await refetch?.();
    } catch {
      message.error(t('reviews.keepError', 'Failed to keep feedback'));
    }
  };

  return (
    <Modal
      open={isModalOpen}
      onCancel={() => handleCloseModal?.()}
      closable={false}
      footer={null}
      centered
      destroyOnClose={true}
      width={716}
      data-testid="keep-feedback-modal"
    >
      <div className="flex flex-col gap-6 py-4 px-2" data-testid="keep-feedback-modal-content">
        <div className="flex flex-col gap-2" data-testid="keep-feedback-header-container">
          <TitleText
            text={t('reviews.keepFeedback')}
            size="2xl"
            className="!text-[#111928] !font-semibold"
          />
          <InfoText text={t('reviews.keepFeedback subTitle')} className='!text-[#637381] dark:!text-dark-text-secondary' />
        </div>

        <FormProvider {...methods}>
          <form className="w-full" onSubmit={methods.handleSubmit(onSubmit)} data-testid="keep-feedback-form">
            <div className="w-full h-60" data-testid="keep-feedback-textarea-container">
              <FormField
                id="comments"
                name="comments"
                type="textarea"
                placeholder={t('dispute.EnterComments')}
                data-testid="keep-feedback-modal-comments-textarea"
                className="w-full !h-40 bg-transparent"
                maxLength={50}
                label={t('comment')}
                helperText={methods.formState.errors.comments?.message || t('dispute.messageInterview', {
                  min: 2,
                  max: 50,
                })}
              />
            </div>
            <div className="flex justify-end  w-full gap-6" data-testid="keep-feedback-actions-container">
              <Button
                type="button"
                onClick={() => handleCloseModal?.()}
                variant="outline"
                data-testid="keep-feedback-cancel-btn"
              >
                {t('home.cancel')}
              </Button>

              <Button type="submit" disabled={isLoading} data-testid="keep-feedback-submit-btn">
                {t('home.send')}
              </Button>
            </div>
          </form>
        </FormProvider>
      </div>
    </Modal>
  );
}
